////////////////////////////////////////////
// useEvmWallet — THE APP'S ROBINHOOD CHAIN WALLET SURFACE
////////////////////////////////////////////
//
// WRAPS wagmi IN THE SAME SHAPE AS useSolWallet. WRITES ENSURE THE WALLET IS
// ON THE ACTIVE RH CHAIN FIRST (wagmi switchChain HANDLES THE ADD-CHAIN
// PROMPT BECAUSE BOTH RH CHAINS ARE IN THE CONFIG); A WALLET THAT CANNOT
// REACH THE CHAIN GETS THE TAGGED rhWrongChain ERROR THE UI COPY EXPECTS.
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useAccount, useConnect, useConnectors, useDisconnect,
  useSignMessage, useSwitchChain, useWriteContract, usePublicClient,
} from "wagmi";
import type { Abi } from "viem";
import * as log from "@/lib/log";
import { postJson } from "@/lib/api/client";
import { useNetwork } from "@/config/useNetwork";

// WRONG-CHAIN TAG — DISTINGUISHES "CAN'T REACH ROBINHOOD CHAIN" FROM A
// GENERIC USER CANCEL (SAME SHAPE THE VANILLA friendlyErrorRh CHECKS)
export interface RhWrongChainError extends Error { rhWrongChain: true }

function tagWrongChain(e: unknown): RhWrongChainError {

  const err = (e instanceof Error ? e : new Error(String((e as { message?: string })?.message || e))) as RhWrongChainError;
  err.rhWrongChain = true;
  return err;
}

// SEEN PING — EVM MIRROR OF THE SOLANA ONE, SEPARATE SESSION KEY SO A VISITOR
// CONNECTING BOTH WALLET KINDS PINGS ONCE PER WALLET
function pingSeen(wallet: string) {

  try {
    if (sessionStorage.getItem("fuze:seen-evm") === wallet) return;
    sessionStorage.setItem("fuze:seen-evm", wallet);
  } catch { /* SESSIONSTORAGE BLOCKED — STILL PING BELOW */ }

  postJson("/api/users/seen", { wallet }).catch(() => { /* FIRE-AND-FORGET */ });
  log.pingSession();
}

export function useEvmWallet() {

  const net = useNetwork();
  const account = useAccount();
  const { connectAsync } = useConnect();
  const connectors = useConnectors();
  const { disconnect, disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient({ chainId: net.rh.chainId });

  const address = account.address ?? null;

  // CONNECT/DISCONNECT BREADCRUMBS + SEEN PING
  const prevRef = useRef<string | null>(null);
  useEffect(() => {

    const prev = prevRef.current;
    if (address && address !== prev) {

      log.info("wallet-evm", prev ? "account changed" : "connected", { wallet: shortEvm(address) });
      pingSeen(address);
    }
    if (!address && prev)
      log.info("wallet-evm", "disconnected", { wallet: shortEvm(prev) });
    prevRef.current = address;
  }, [address]);

  // ENSURE CHAIN — SWITCH (OR ADD) THE ACTIVE RH CHAIN BEFORE ANY WRITE
  const ensureChain = useCallback(async () => {

    if (account.chainId === net.rh.chainId) return;

    try {
      await switchChainAsync({ chainId: net.rh.chainId });
    } catch (e) {
      log.warn("wallet-evm", "chain switch failed", { err: (e as Error)?.message });
      throw tagWrongChain(e);
    }
  }, [account.chainId, net.rh.chainId, switchChainAsync]);

  // WRITE CONTRACT — THE CREATE/COMMIT/SWAP PATH (SIGN+BROADCAST IN ONE
  // PROMPT; CALLERS AWAIT THE RECEIPT VIA waitForTx IF THEY NEED MINING)
  const writeContract = useCallback(async (args: {
    address: `0x${string}`;
    abi: Abi;
    functionName: string;
    args?: readonly unknown[];
    value?: bigint;
  }): Promise<`0x${string}`> => {

    if (!address) throw new Error("wallet not connected");

    await ensureChain();
    try {
      const hash = await writeContractAsync({
        address: args.address,
        abi: args.abi,
        functionName: args.functionName,
        args: args.args as unknown[],
        value: args.value,
        chainId: net.rh.chainId,
      });
      log.info("wallet-evm", "tx sent", { method: args.functionName, hash });
      return hash;
    } catch (e) {
      const err = e as { code?: number | string; message?: string };
      const cancelled = err && (err.code === 4001 || err.code === "ACTION_REJECTED" || /reject|cancel|denied/i.test(err.message || ""));
      if (cancelled) log.info("wallet-evm", "tx cancelled", { method: args.functionName });
      else log.error("wallet-evm", "tx failed", { method: args.functionName, err: err?.message, code: err?.code });
      throw e;
    }
  }, [address, ensureChain, writeContractAsync, net.rh.chainId]);

  // WAIT FOR RECEIPT — MINED CONFIRMATION FOR FLOWS THAT DECODE EVENTS.
  // viem RESOLVES EVEN FOR status:"reverted" (UNLIKE ethers tx.wait(), WHICH
  // REJECTED) — WITHOUT THIS CHECK A MINED-BUT-REVERTED COMMIT/SWAP/CREATE
  // WOULD REPORT SUCCESS TO THE USER (money-path review 2026-07-18).
  const waitForTx = useCallback(async (hash: `0x${string}`) => {

    if (!publicClient) throw new Error("no public client");

    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") {

      log.error("wallet-evm", "tx reverted", { hash });
      // SAME MESSAGE SHAPE ethers THREW — friendlyErrorRh PASSES IT THROUGH
      throw new Error("transaction execution reverted");
    }
    return receipt;
  }, [publicClient]);

  return useMemo(() => ({
    address,
    short: shortEvm(address),
    connected: !!address,
    chainId: account.chainId ?? null,
    // WAGMI LIFECYCLE — 'connecting' | 'reconnecting' | 'connected' |
    // 'disconnected' (THE CONTROLLER NORMALIZES BOTH CHAINS OFF THIS)
    status: account.status,
    // ACTIVE CONNECTOR uid — LETS THE SIGN-IN FLOW TELL "CLICKED THE CONNECTED
    // WALLET" FROM "CLICKED A DIFFERENT ONE" (wagmi HOLDS ONE ACTIVE CONNECTION)
    connectorUid: account.connector?.uid ?? null,
    // WALLET BRAND FOR COPY ("MetaMask", "Rabby", …) — null WHEN DISCONNECTED
    // OR WHEN THE CONNECTOR IS A TRANSPORT, NOT A BRAND (WalletConnect PAIRING
    // NAMES THE PROTOCOL, NOT THE APP); CALLERS FALL BACK TO "your wallet"
    walletName: walletBrandName(account.connector?.name),
    connectors,
    connectAsync,
    disconnect,
    disconnectAsync,
    // 0x-HEX SIGNATURE (EIP-191) — THE SERVER VERIFIES IT WITH viem verifyMessage
    signMessage: (message: string) => signMessageAsync({ message }),
    ensureChain,
    writeContract,
    waitForTx,
    publicClient,
  }), [address, account.chainId, account.status, account.connector?.uid, account.connector?.name, connectors, connectAsync, disconnect, disconnectAsync, signMessageAsync, ensureChain, writeContract, waitForTx, publicClient]);
}

function shortEvm(a: string | null): string | null {

  return a ? a.slice(0, 6) + "…" + a.slice(-4) : null;
}

// CONNECTOR NAME → BRAND FOR USER-FACING COPY. wagmi's injected connector
// reports the real wallet ("MetaMask", "Rabby Wallet", "OKX Wallet"); the
// generic fallbacks ("Injected", "WalletConnect", "") name a transport, not a
// wallet, so we return null and callers say "your wallet" instead.
function walletBrandName(name: string | undefined): string | null {

  if (!name) return null;
  if (/^injected$/i.test(name) || /walletconnect/i.test(name)) return null;
  return name;
}
